// @flow

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, TabBasic, TabBasicItem } from '@riseart/common';
import { useAclAccess } from '@riseart/fe-utils';
import { application as CONFIG_APP } from 'Config';
import { selectAclRole } from 'shared_services/redux/selectors/auth';
import { LoginForm } from 'shared_components/forms/login/Form';
import { HOCFormLogin } from 'shared_hocs/forms/Login';
import { RegisterForm } from 'shared_components/forms/register/Form';
import { HOCFormRegister } from 'shared_hocs/forms/Register';
import { AuthContainer, AuthBody } from 'shared_components/auth/containers/auth/Auth';
import { SocialContainer } from 'shared_components/auth/containers/social/Social';
import { HOCSocialLogin } from 'shared_hocs/auth/SocialLogin';
import { useAuthParams } from 'shared_data/hooks/auth/useAuthParams';

import { authTabsCls, authTabsSectionMainCls } from 'shared_components/common/auth/styles.less';

const AUTH_TYPE = {
  login: 'login',
  join: 'join',
};

const HOCLoginSocial = HOCSocialLogin(SocialContainer);
const HOCLoginForm = HOCFormLogin(LoginForm);
const HOCRegisterForm = HOCFormRegister(RegisterForm);

type Props = {
  resourcePermissions: string | Array<string>,
  authType?: string,
  rolesHierarchy?: Object,
  children?: Node,
  skipRedirect?: boolean,
};

/**
 * AclComponentGateway
 * A component that checks if a visitor is allowed to access the children based on ACL permissions
 *
 * @param {Props} props
 * @returns {Node}
 */
export function AclComponentGateway({
  children,
  resourcePermissions,
  authType = AUTH_TYPE.login,
  rolesHierarchy = CONFIG_APP.acl.rolesHierarchy,
  skipRedirect = false,
}: Props) {
  const [type, setType] = useState(authType);
  const aclRole = useSelector(selectAclRole);
  const { isAllowed } = useAclAccess({ role: aclRole, resourcePermissions, rolesHierarchy });
  const authParams = useAuthParams();

  if (isAllowed) {
    return children;
  }

  return (
    <div className={authTabsCls}>
      <AuthContainer sectionProps={{ className: authTabsSectionMainCls }}>
        {(onLoadingChange) => (
          <FormattedMessage id="components.auth.signupTab">
            {(joinLinkText: string) => (
              <FormattedMessage id="components.auth.loginTab">
                {(loginLabel: string) => (
                  <TabBasic active={type}>
                    <TabBasicItem
                      tabid={AUTH_TYPE.join}
                      link={
                        <Button
                          type="link"
                          linkType="strong"
                          onClick={() => setType(AUTH_TYPE.join)}
                        >
                          {joinLinkText}
                        </Button>
                      }
                    >
                      {type === AUTH_TYPE.join ? (
                        <AuthBody>
                          <HOCLoginSocial
                            isLogin={false}
                            align="vertical"
                            onLoadingChange={onLoadingChange}
                            authParams={authParams}
                            skipRedirect={skipRedirect}
                          />
                          <HOCRegisterForm
                            onLoadingChange={onLoadingChange}
                            showLegalInfo
                            showLogin={false}
                            authParams={authParams}
                            skipRedirect={skipRedirect}
                          />
                        </AuthBody>
                      ) : null}
                    </TabBasicItem>
                    <TabBasicItem
                      tabid={AUTH_TYPE.login}
                      link={
                        <Button
                          type="link"
                          linkType="strong"
                          onClick={() => setType(AUTH_TYPE.login)}
                        >
                          {loginLabel}
                        </Button>
                      }
                    >
                      {type === AUTH_TYPE.login ? (
                        <AuthBody>
                          <HOCLoginSocial
                            isLogin
                            align="vertical"
                            onLoadingChange={onLoadingChange}
                            authParams={authParams}
                            skipRedirect={skipRedirect}
                          />
                          <HOCLoginForm
                            onLoadingChange={onLoadingChange}
                            authParams={authParams}
                            showSignup={false}
                            skipRedirect={skipRedirect}
                          />
                        </AuthBody>
                      ) : null}
                    </TabBasicItem>
                  </TabBasic>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        )}
      </AuthContainer>
    </div>
  );
}
